/**
 * Company: SPHMedia
 * Description: [Gets Short Form videos data]
 */

import fetchBHApi from "@helper/fetchBHApi";
import {
  ENABLE_MEDIA_CONTENT_INFINITE_SCROLL,
  HOMEPAGE_PORTRAIT_VIDEO_SECTION,
} from "@helper/getEnvVariables";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { PlaylistType } from "@typings/Playlist.d";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type useShortFormVideoData = {
  playing: LatestVideoResponse | undefined;
  videos: LatestVideoResponse[];
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  handleOnEndedVideo: () => void;
};

/**
 * Custom React Hook: useShortFormVideo
 * Description: [Gets Short Form videos from bc playlist]
 * @returns data - The data returned by the hook
 */
export default function useShortFormVideo({
  parentRef,
  limit = 6,
}: {
  parentRef: React.RefObject<HTMLDivElement | null>;
  limit?: number;
}): useShortFormVideoData {
  const [playlistVideos, setPlaylistVideos] = useState<LatestVideoResponse[]>();
  const [videos, setVideos] = useState<LatestVideoResponse[]>();
  const [playing, setPlaying] = useState<LatestVideoResponse | undefined>();
  const hasInitialized = useRef(false);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setVideos(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setVideos],
  );

  const handleOnClickThumbnail = useCallback(
    (props: LatestVideoResponse) => {
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },

    [handleNowPlaying, setPlaying],
  );

  const handleOnEndedVideo = useCallback(() => {
    if (!playlistVideos) return;

    const playlistLength = playlistVideos.length;
    if (playlistLength === 0) return;

    let index: number = playlistVideos.findIndex(
      (video) => video.id === playing?.id,
    );

    index++; // Move to the next video in the playlist
    handleNowPlaying(playlistVideos[index % playlistLength].id);
    setPlaying(playlistVideos[index % playlistLength]);
  }, [handleNowPlaying, playlistVideos, playing]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const videos: LatestVideoResponse[] = await fetchBHApi<
          LatestVideoResponse[]
        >(
          `latest-videos-playlist?playlistId=${PlaylistType.SHORT_FORM}`,
          "GET",
        );
        const limitedContent = videos.slice(0, limit);
        setPlaylistVideos(limitedContent);
        setVideos(limitedContent);
      } catch (error) {
        console.error("Something went wrong", error);
      }
    };
    if (
      ENABLE_MEDIA_CONTENT_INFINITE_SCROLL ||
      HOMEPAGE_PORTRAIT_VIDEO_SECTION
    ) {
      void fetchData();
    }
  }, [limit]);

  useEffect(() => {
    const element = parentRef.current;

    if (!element || !playlistVideos?.length || hasInitialized.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.25 &&
          !hasInitialized.current
        ) {
          hasInitialized.current = true;

          setPlaying(playlistVideos[0]);
          handleNowPlaying(playlistVideos[0].id);

          observer.disconnect();
        }
      },
      {
        threshold: [0.25],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [parentRef, playlistVideos, handleNowPlaying]);

  return {
    playing,
    videos: videos || [],
    handleOnClickThumbnail,
    handleOnEndedVideo,
  };
}
